import { ChangeEvent, FormEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { SelectChangeEvent } from '@mui/material';

import { errorNormalizer } from '../../../../../shared/Helpers/functions';
import { CustomAlert, CustomButton, CustomInput, CustomSelect, Text } from '../../../../../shared/uiComponents';
import { EditSvg } from '../../../../../shared/Assets/svgIcons';
import { ButtonWrapper } from '../../../../../components/StyledComponents';

import { DispatchProperties, useSelector } from '../../../../../redux/store';
import {
	ProgramDataProperties,
	UpdateProgramProperties,
} from '../../../../../redux/API/ClientAPIHelpers/clientProgramProperties';
import { updateProgram } from '../../../../../redux/State/clientSlice/clientProgramSlice';
import { useProgramsContext } from '../ProgramsContext';

const EditContent = ({ data, onClose }: { data: ProgramDataProperties; onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { fetchData } = useProgramsContext();
	const skillAreas = useSelector((state) => state.skillArea.skillAreas);

	const [name, setName] = useState<string>(data.name);
	const [goalName, setGoalName] = useState<string>(data.goalName);
	const [skillAreaId, setSkillAreaId] = useState<string>(data.skillAreaId);

	const onProgramNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setName(value);
	};

	const onGoalNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setGoalName(value);
	};

	const onSkillAreaChange = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setSkillAreaId(value);
	};

	const editProgramHandler = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const editeData: UpdateProgramProperties = {
			name,
			goalName,
			skillAreaId,
		};

		dispatch(updateProgram({ data: editeData, id: data.id }))
			.then(unwrapResult)
			.then(() => {
				fetchData();
				onClose();
			})
			.catch(errorNormalizer);
	};

	return (
		<form onSubmit={editProgramHandler}>
			<CustomSelect
				label="Skill Area:"
				data={skillAreas}
				value={skillAreaId}
				setValue={onSkillAreaChange}
				className="marginBottom8"
			/>
			<CustomInput label="Program name:" value={name} onChange={onProgramNameChange} className="marginBottom8" />
			<CustomInput label="Goal name:" value={goalName} onChange={onGoalNameChange} className="marginBottom24" />
			<ButtonWrapper isInModal>
				<CustomButton title="Save" type="submit" disabled={!name || !goalName || !skillAreaId} />
				<CustomButton title="Cancel" onClick={onClose} buttonType="secondaryGray" />
			</ButtonWrapper>
		</form>
	);
};

const EditProgram = ({ data, onlyIcon }: { data: ProgramDataProperties; onlyIcon?: boolean }) => {
	const [open, setOpen] = useState<boolean>(false);
	const openHandler = () => setOpen(true);
	const closeHandler = () => setOpen(false);
	const isUpdating = useSelector((state) => state.clientProgram.isUpdating);

	return (
		<>
			<div className="actionRow" onClick={openHandler}>
				<div className="actionIcon">
					<EditSvg color="#62615F" />
				</div>
				{!onlyIcon && (
					<div className="actionContent">
						<Text title="Edit" type="psm" fontWeight="medium" />
					</div>
				)}
			</div>
			<CustomAlert
				isBusy={isUpdating}
				title="Edit Program Information"
				open={open}
				onClose={closeHandler}
				Content={({ onClose }) => <EditContent data={data} onClose={onClose} />}
			/>
		</>
	);
};

export default EditProgram;
